import { useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { MoreHorizontalIcon } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";

import {
  Column,
  RichTextDisplay,
  RichTextEditorInput,
  Row,
  Text,
} from "@/components";
import { useUserSheet } from "@/components/user-sheet/userSheetContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";

import { useTaggableUsers } from "@/contexts/TaggableUsersContext";
import { useDeleteRemoveUserNote } from "@/hooks/deleteRemoves/useDeleteRemoveUserNote";
import { useGetCurrentUser } from "@/hooks/queries/useGetCurrentUser";
import { useUpdateSetUserNote } from "@/hooks/updateSets/useUpdateSetUserNote";
import { dates } from "@/lib/dates";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { UserNote } from "@/types";

const AUTOMATED_USER_ID = 6;
const NOTE_MODIFY_WINDOW_MINUTES = 15;

const updateNoteSchema = z.object({
  content: z.string().min(1, "Required"),
});

export const NoteItem = ({ note }: { note: UserNote }) => {
  /* Context */
  const { isReadOnly } = useUserSheet();

  /* Database CRUD Operations */
  // Update
  const { mutate: updateNote, isPending: isUpdating } = useUpdateSetUserNote();

  // Delete
  const { mutate: deleteNote } = useDeleteRemoveUserNote();

  /* React Hooks */
  const [isEditing, setIsEditing] = useState(false);
  const currentUser = useGetCurrentUser();
  const { userNameMap } = useTaggableUsers();
  const form = useForm<z.infer<typeof updateNoteSchema>>({
    defaultValues: { content: note.content },
    resolver: zodResolver(updateNoteSchema),
  });

  /* Helpers */
  // Migrated legacy notes are owned by the automated user.
  const isArchived = note.ownerId === AUTOMATED_USER_ID;
  const canModify =
    !isReadOnly &&
    note.ownerId === currentUser?.id &&
    dates.calculateMinutesAgo(note.createdAt) < NOTE_MODIFY_WINDOW_MINUTES;
  const contextLine = [note.jobTitle, note.companyName]
    .filter(Boolean)
    .join(" · ");
  const authorName = isArchived
    ? "Automated"
    : userNameMap[note.ownerId] || "Unknown";

  /* Handlers */
  const handleStartEdit = () => {
    mixpanelUtils.trackButtonClick(
      "Edit Note",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
    );
    form.reset({ content: note.content });
    setIsEditing(true);
  };

  const handleSubmitEdit = (data: z.infer<typeof updateNoteSchema>) => {
    if (isUpdating) return;

    mixpanelUtils.trackFormSubmission(
      "Update Note",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
    );
    updateNote(
      { content: data.content, noteId: note.id },
      {
        onSuccess: () => {
          setIsEditing(false);
        },
      },
    );
  };

  const handleDelete = () => {
    mixpanelUtils.trackButtonClick(
      "Delete Note",
      MIXPANEL_SCREENS.CANDIDATE_SHEET,
    );
    deleteNote({ noteId: note.id });
  };

  /* Render */
  const renderBody = () => {
    if (isEditing) {
      return (
        <Form {...form}>
          <form
            className="flex flex-col gap-2"
            // eslint-disable-next-line @typescript-eslint/no-misused-promises
            onSubmit={form.handleSubmit(handleSubmitEdit)}
          >
            <FormField
              control={form.control}
              name="content"
              render={({ field }) => (
                <FormItem>
                  <FormControl>
                    <RichTextEditorInput
                      editorContentClasses="max-h-[200px] min-h-[72px] px-2 py-1 text-sm"
                      mode={{
                        onChange: field.onChange,
                        type: "html",
                        value: field.value,
                      }}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <Row className="justify-end gap-2">
              <Button
                onClick={() => {
                  setIsEditing(false);
                }}
                size="sm"
                type="button"
                variant="outlineSecondary"
              >
                Cancel
              </Button>
              <Button disabled={isUpdating} size="sm" type="submit">
                Save
              </Button>
            </Row>
          </form>
        </Form>
      );
    }

    return (
      <RichTextDisplay
        className="text-sm font-normal leading-relaxed text-slate-700"
        richText={note.content}
      />
    );
  };

  const renderActionsMenu = () => {
    if (!canModify || isEditing) return null;

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            className="h-6 w-6 text-slate-400 hover:text-slate-700"
            size="icon"
            variant="ghost"
          >
            <MoreHorizontalIcon className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={handleStartEdit}>
            Edit note
          </DropdownMenuItem>
          <DropdownMenuItem
            className="text-red-600 focus:text-red-600"
            onClick={handleDelete}
          >
            Delete note
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const render = () => {
    return (
      <Column className="gap-1.5 border-b border-slate-100 py-4 first:pt-0 last:border-b-0">
        <Row className="items-center justify-between">
          <Row className="items-center gap-2">
            <Text className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] font-medium uppercase tracking-wider text-slate-500">
              {dates.formatShortDate(note.createdAt)}
            </Text>
            {contextLine ? (
              <Text className="text-xs font-normal text-slate-500">
                {contextLine}
              </Text>
            ) : null}
            {isArchived ? (
              <Text className="rounded bg-amber-50 px-1.5 py-0.5 font-mono text-[10px] font-medium uppercase tracking-wider text-amber-600">
                Archived
              </Text>
            ) : null}
          </Row>
          {renderActionsMenu()}
        </Row>
        {renderBody()}
        <Text className="text-xs font-normal text-slate-500">{authorName}</Text>
      </Column>
    );
  };

  return render();
};
